import sanitizeHtml from "sanitize-html";

// Tags whose entire content (not just the tag itself) must be dropped silently.
// These tags contain no user-visible text and may carry executable or embedded
// content that must never reach the editor.
const DROP_WITH_CONTENT = ['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'base'];

// Tags that are allowed in the sanitized output.  Used both as the allowedTags
// option and inside the '*' transform so the two lists are always in sync.
const ALLOWED_TAGS = ['p','br','ul','ol','li','b','strong','i','em','span'];

export const sanitizeHtmlString = (dirty) => {
    if(!dirty) {
        return dirty;
    }
    return sanitizeHtml(dirty, {
        allowedTags: ALLOWED_TAGS,
        allowedAttributes: {
            a: ['href', 'title', 'target', 'rel']
        },
        // only allow http(s) and mailto
        allowedSchemes: ['http','https','mailto'],
        allowedSchemesByTag: {
            a: ['http','https','mailto']
        },
        allowProtocolRelative: false,
        // Tags listed here have their raw text content treated as opaque (not
        // parsed) and, since they are not in allowedTags, they and their content
        // are dropped entirely without emitting any placeholder text.
        // NOTE: nonTextTags works only when the tag name is preserved through
        //       transforms, which is why the '*' wildcard below must NOT rename
        //       these tags.
        nonTextTags: DROP_WITH_CONTENT,
        transformTags: {
            // Images → silently removed; preserve alt text as visible text so
            // the author knows something was stripped.
            'img': (tagName, attribs) => {
                const alt = (attribs.alt || '').trim();
                return alt
                    ? { tagName: 'span', text: alt }
                    : false;
            },

            // Fallback: any tag not handled above gets its unsafe attributes
            // stripped and its tag name normalised to one of the allowed tags.
            // Text content and safe child elements are preserved.
            '*': (tagName, attribs) => {
                // Tags in DROP_WITH_CONTENT must keep their original name so
                // that sanitize-html's nonTextTags mechanism can drop them and
                // their content completely.  Do not rename them here.
                if (DROP_WITH_CONTENT.includes(tagName.toLowerCase())) {
                    return { tagName, attribs: {} };
                }

                // Remove event-handler attributes and inline styles everywhere.
                const clean = {};
                for (const k of Object.keys(attribs || {})) {
                    if (/^on/i.test(k)) continue;
                    if (k === 'style' || k === 'srcset') continue;
                    clean[k] = attribs[k];
                }
                // Keep the tag if it is already in the allowed set; otherwise
                // unwrap it to a plain <span> so its text children are retained.
                const newTag = ALLOWED_TAGS.includes(tagName.toLowerCase()) ? tagName : 'span';
                return { tagName: newTag, attribs: clean };
            }
        },
    })
}